import React, { useState, useEffect } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useCRM } from '../../contexts/CRMContext';
import { useLocation } from '../../contexts/LocationContext';
import api from '../../utils/api';
import ImportExportButtons from '../../components/common/ImportExportButtons';

const Customers = () => {
  const {
    customers,
    loading,
    error,
    pagination,
    getCustomers,
    createCustomer,
    updateCustomer,
    deleteCustomer
  } = useCRM();
  
  const { selectedLocation } = useLocation();
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [segmentFilter, setSegmentFilter] = useState('');
  
  const [openModal, setOpenModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [currentCustomer, setCurrentCustomer] = useState(null);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    segment: 'new',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    notes: ''
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);

  // Fetch customers when location changes
  useEffect(() => {
    if (selectedLocation) {
      getCustomers(selectedLocation.id, page + 1, rowsPerPage, searchTerm, segmentFilter);
    }
  }, [selectedLocation, page, rowsPerPage, searchTerm, segmentFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleSegmentFilterChange = (event) => {
    setSegmentFilter(event.target.value);
    setPage(0);
  };

  const handleOpenCreateModal = () => {
    setModalMode('create');
    setCurrentCustomer(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      segment: 'new',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: ''
      },
      notes: ''
    });
    setFormErrors({});
    setOpenModal(true);
  };

  const handleOpenEditModal = (customer) => {
    setModalMode('edit');
    setCurrentCustomer(customer);
    setFormData({
      firstName: customer.firstName || '',
      lastName: customer.lastName || '',
      email: customer.email || '',
      phone: customer.phone || '',
      company: customer.company || '',
      segment: customer.segment || 'new',
      address: customer.address || {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: ''
      },
      notes: customer.notes || ''
    });
    setFormErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setCurrentCustomer(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      segment: 'new',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: ''
      },
      notes: ''
    });
    setFormErrors({});
  };

  const handleFormChange = (field, value) => {
    // Clear error for this field when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    
    if (field.startsWith('address.')) {
      const addressField = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    if (modalMode === 'create') {
      const result = await createCustomer({
        locationId: selectedLocation.id,
        ...formData
      });
      
      if (result.success) {
        handleCloseModal();
      }
    } else {
      const result = await updateCustomer(currentCustomer.id, formData);
      
      if (result.success) {
        handleCloseModal();
      }
    }
  };

  const handleDeleteCustomer = async (id) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      await deleteCustomer(id);
    }
  };

  const getSegmentColor = (segment) => {
    switch (segment?.toLowerCase()) {
      case 'new': return 'default';
      case 'returning': return 'primary';
      case 'loyal': return 'success';
      case 'at-risk': return 'warning';
      case 'lost': return 'error';
      default: return 'default';
    }
  };

  // CSV Export - exports ALL customers from backend
  const handleExport = async () => {
    const locationId = selectedLocation?.id || selectedLocation?._id;
    if (!locationId) {
      alert('Please select a location first');
      return;
    }

    setExporting(true);
    try {
      const response = await api.get(`/customers/export?locationId=${locationId}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `customers_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export customers');
    } finally {
      setExporting(false);
    }
  };

  // CSV Import - uses backend import endpoint
  const handleImport = async (file) => {
    const locationId = selectedLocation?.id || selectedLocation?._id;
    if (!locationId) {
      alert('Please select a location first');
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('locationId', locationId);

      const response = await api.post('/customers/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        alert(response.data.message);
        // Refresh the list
        getCustomers(locationId, page + 1, rowsPerPage, searchTerm, segmentFilter);
      } else {
        alert(`Import failed: ${response.data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Import error:', error);
      alert(`Import failed: ${error.response?.data?.error || error.message}`);
    } finally {
      setImporting(false);
    }
  };

  if (loading && customers.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Customer Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your customer database and track interactions
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search customers..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Segment</InputLabel>
              <Select
                value={segmentFilter}
                onChange={handleSegmentFilterChange}
                label="Segment"
              >
                <MenuItem value="">All Segments</MenuItem>
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="returning">Returning</MenuItem>
                <MenuItem value="loyal">Loyal</MenuItem>
                <MenuItem value="at-risk">At Risk</MenuItem>
                <MenuItem value="lost">Lost</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <ImportExportButtons
                onExport={handleExport}
                onImport={handleImport}
                loading={exporting || importing}
                disabled={!selectedLocation}
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenCreateModal}
                size="small"
              >
                Add
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper>
        <TableContainer
          sx={{
            overflowX: 'auto',
            '& table': {
              minWidth: { xs: 650, sm: 750 }
            }
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Email</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Phone</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Company</TableCell>
                <TableCell>Segment</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Orders</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Total Spent</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customers.map((customer) => (
                <TableRow key={customer.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {customer.firstName} {customer.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' } }}>
                        {customer.email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Typography variant="body2">{customer.email}</Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Typography variant="body2">{customer.phone}</Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Typography variant="body2">{customer.company}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={customer.segment || 'New'} 
                      color={getSegmentColor(customer.segment)} 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Typography variant="body2">{customer.orderCount || 0}</Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Typography variant="body2">
                      ${Number(customer.totalSpent || 0).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                      size="small" 
                      onClick={() => handleOpenEditModal(customer)}
                      sx={{ minWidth: 44, minHeight: 44 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handleDeleteCustomer(customer.id)}
                      sx={{ minWidth: 44, minHeight: 44 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {customers.length === 0 && !loading && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No customers found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Add your first customer to get started
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateModal}
              sx={{ mt: 2 }}
            >
              Add Customer
            </Button>
          </Box>
        )}
        
        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={pagination.totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <Dialog 
        open={openModal} 
        onClose={handleCloseModal} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {modalMode === 'create' ? 'Add New Customer' : 'Edit Customer'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                variant="outlined"
                value={formData.firstName}
                onChange={(e) => handleFormChange('firstName', e.target.value)}
                error={!!formErrors.firstName}
                helperText={formErrors.firstName}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                variant="outlined"
                value={formData.lastName}
                onChange={(e) => handleFormChange('lastName', e.target.value)}
                error={!!formErrors.lastName}
                helperText={formErrors.lastName}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                variant="outlined"
                value={formData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
                error={!!formErrors.email}
                helperText={formErrors.email}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                variant="outlined"
                value={formData.phone}
                onChange={(e) => handleFormChange('phone', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company"
                variant="outlined"
                value={formData.company}
                onChange={(e) => handleFormChange('company', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Segment</InputLabel>
                <Select
                  value={formData.segment}
                  onChange={(e) => handleFormChange('segment', e.target.value)}
                  label="Segment"
                >
                  <MenuItem value="new">New</MenuItem>
                  <MenuItem value="returning">Returning</MenuItem>
                  <MenuItem value="loyal">Loyal</MenuItem>
                  <MenuItem value="at-risk">At Risk</MenuItem>
                  <MenuItem value="lost">Lost</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address"
                variant="outlined"
                value={formData.address?.street || ''}
                onChange={(e) => handleFormChange('address.street', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                variant="outlined"
                value={formData.address?.city || ''}
                onChange={(e) => handleFormChange('address.city', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="State"
                variant="outlined"
                value={formData.address?.state || ''}
                onChange={(e) => handleFormChange('address.state', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Zip Code"
                variant="outlined"
                value={formData.address?.zipCode || ''}
                onChange={(e) => handleFormChange('address.zipCode', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                variant="outlined"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 
             modalMode === 'create' ? 'Create Customer' : 'Update Customer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Customers;



